import React, { useState, useEffect, useRef } from 'react';
import {
  Sun, Moon, LogOut, ArrowRight, Mic,
  Bot, CheckCircle2, Zap, Shield
} from 'lucide-react';
import CapyBotMascot from '../components/landing/CapyBotMascot';
import HowItWorksDiagram from '../components/landing/HowItWorksDiagram';
import WaitingListModal from '../components/WaitingListModal';

// --- STYLES & FONTS ---
const FontStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,300;1,9..144,400&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');
    
    .font-heading {
      font-family: 'Fraunces', serif;
      font-variation-settings: "SOFT" 50, "WONK" 0;
    }
    .font-body {
      font-family: 'Plus Jakarta Sans', sans-serif;
    }
    
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-fade-in-up {
      animation: fadeInUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
    }
  `}</style>
);

// --- UTILITIES ---

const LatticePattern = () => (
  <svg className="absolute inset-0 -z-10 h-full w-full opacity-[0.04] dark:opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="lattice-pattern" width="60" height="60" patternUnits="userSpaceOnUse">
        <path d="M0 30 Q30 0 60 30 T120 30" fill="none" stroke="currentColor" strokeWidth="0.8" className="text-stone-900 dark:text-slate-100" />
        <path d="M0 30 Q30 60 60 30 T120 30" fill="none" stroke="currentColor" strokeWidth="0.8" className="text-stone-900 dark:text-slate-100" />
        <circle cx="30" cy="30" r="1.5" className="fill-stone-400 dark:fill-slate-500" />
        <circle cx="60" cy="0" r="1.5" className="fill-stone-400 dark:fill-slate-500" />
        <circle cx="60" cy="60" r="1.5" className="fill-stone-400 dark:fill-slate-500" />
        <circle cx="0" cy="0" r="1.5" className="fill-stone-400 dark:fill-slate-500" />
        <circle cx="0" cy="60" r="1.5" className="fill-stone-400 dark:fill-slate-500" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#lattice-pattern)" />
    <rect width="100%" height="100%" fill="url(#radial-fade)" />
    <defs>
      <radialGradient id="radial-fade" cx="50%" cy="50%" r="60%">
        <stop offset="0%" stopColor="white" stopOpacity="0" className="dark:stop-color-slate-950" />
        <stop offset="100%" stopColor="white" stopOpacity="0.6" className="dark:stop-color-slate-950" />
      </radialGradient>
    </defs>
  </svg>
);

// --- SUB COMPONENTS ---

const NavLink = ({ href, children }) => (
  <a
    href={href}
    className="font-body text-stone-600 dark:text-slate-300 hover:text-stone-900 dark:hover:text-white text-sm font-semibold transition-colors duration-200"
  >
    {children}
  </a>
);

const Button = ({ children, variant = 'primary', className = '', icon: Icon, ...props }) => {
  const baseStyles = "font-body inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold transition-all duration-200 active:scale-95 text-sm tracking-wide whitespace-nowrap";

  const variants = {
    primary: "bg-stone-900 dark:bg-slate-100 text-white dark:text-stone-900 hover:bg-stone-800 dark:hover:bg-white hover:shadow-lg shadow-stone-900/10",
    secondary: "bg-transparent text-stone-900 dark:text-white border border-stone-200 dark:border-slate-700 hover:border-stone-900 dark:hover:border-white hover:bg-stone-50 dark:hover:bg-slate-800",
    ghost: "text-stone-600 dark:text-slate-200 hover:bg-stone-100 dark:hover:bg-slate-800",
  };

  return (
    <button className={`${baseStyles} ${variants[variant]} ${className}`} {...props}>
      {children}
      {Icon && <Icon className="ml-2 w-4 h-4" />}
    </button>
  );
};

// UPDATED BENTO CARD: Uniform style & internal glow for ALL cards
const BentoCard = ({ icon: Icon, title, desc, className = "" }) => (
  <div className={`group relative p-8 md:p-10 bg-white dark:bg-slate-900 border border-stone-100 dark:border-slate-800 hover:border-stone-300 dark:hover:border-slate-600 transition-all duration-500 flex flex-col justify-between shadow-sm hover:shadow-xl hover:-translate-y-1 h-full w-full overflow-hidden rounded-3xl ${className}`}>

    {/* Content (z-10 to stay above shadow) */}
    <div className="relative z-10">
      <div className="w-14 h-14 rounded-full bg-stone-50 dark:bg-slate-800 flex items-center justify-center mb-6 group-hover:bg-stone-100 dark:group-hover:bg-slate-700 transition-colors duration-300 border border-stone-100 dark:border-slate-700">
        <Icon className="w-6 h-6 text-stone-700 dark:text-slate-300 transition-colors" />
      </div>
      <h3 className="font-heading text-2xl md:text-3xl font-medium text-stone-900 dark:text-white mb-3 tracking-tight">{title}</h3>
      <p className="font-body text-stone-500 dark:text-slate-400 text-base md:text-lg leading-relaxed font-medium">{desc}</p>
    </div>

    {/* The Shade / Glow Effect */}
    <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 w-64 h-64 md:w-80 md:h-80 bg-gradient-to-br from-stone-200 to-emerald-50 dark:from-slate-800 dark:to-emerald-900 rounded-full blur-[80px] opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none" />
  </div>
);

const FadeInWhenVisible = ({ children, delay = 0, className = "" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) setIsVisible(true);
      });
    }, { threshold: 0.1 });
    
    const currentRef = domRef.current;
    if (currentRef) observer.observe(currentRef);
    
    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  return (
    <div
      ref={domRef}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-1000 transform ${className} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
    >
      {children}
    </div>
  );
};

// --- MAIN COMPONENT ---

export default function LandingPage({
  darkMode,
  setDarkMode,
  isLoggedIn,
  handleLogout,
  onDeployClick,
  initialWaitlist = false
}) {
  const [scrolled, setScrolled] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(initialWaitlist);

  useEffect(() => {
    if (initialWaitlist) setShowWaitlist(true);
  }, [initialWaitlist]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`${darkMode ? 'dark' : ''} w-full overflow-x-hidden font-body text-stone-900 dark:text-slate-100`}>
      <FontStyles />
      <div className={`min-h-screen transition-colors duration-500 ${darkMode ? 'bg-slate-950' : 'bg-[#FDFCF8]'}`}>

        <LatticePattern />

        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 px-4 md:px-6 py-4 flex justify-center transition-all duration-300 ${scrolled ? 'pt-2' : 'pt-6'}`}>
          <div className={`w-full max-w-5xl rounded-full flex items-center justify-between px-6 py-3 transition-all duration-300 ${scrolled ? 'bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border border-stone-200/50 dark:border-slate-800 shadow-sm' : 'bg-transparent'}`}>

            <div className="flex items-center gap-2 shrink-0">
              <img src="/hicapy.png" alt="HiCapy" className="h-6 w-6 object-contain" onError={(e) => e.target.style.display = 'none'} />
              <span className="font-heading text-lg font-bold tracking-tight text-stone-900 dark:text-white">
                HiCapy
              </span>
            </div>

            <div className="flex items-center gap-2 md:gap-8 flex-nowrap shrink-0">
              <div className="hidden sm:flex items-center gap-6">
                <NavLink href="#features">Capabilities</NavLink>
                <NavLink href="#how-it-works">Method</NavLink>
                <NavLink href="/founders">Founders</NavLink>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setDarkMode(!darkMode)} className="p-2 rounded-full hover:bg-stone-100 dark:hover:bg-slate-800 text-stone-600 dark:text-slate-400 transition-colors shrink-0">
                  {darkMode ? <Sun size={18} /> : <Moon size={18} />}
                </button>
                {isLoggedIn ? (
                  <Button variant="ghost" onClick={handleLogout} className="text-sm px-4 py-2 h-auto border border-stone-200 hidden md:flex">
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </Button>
                ) : (
                  <Button variant="primary" onClick={onDeployClick} className="px-5 py-2.5 h-auto text-xs md:text-sm shadow-none whitespace-nowrap shrink-0">
                    Get Started
                  </Button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-36 md:pt-48 pb-24 px-6 overflow-hidden">
          <div className="container max-w-6xl mx-auto">
            {/* DOM Order: Text First -> Mascot Second. 
                Grid: 1 col mobile, 2 cols desktop. */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 items-center">

              <div className="relative z-10">
                <FadeInWhenVisible>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-100 dark:bg-slate-900 border border-stone-200 dark:border-slate-700 mb-8 w-auto max-w-fit">
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span className="font-body text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-400">System Operational</span>
                  </div>

                  <h1 className="font-heading text-5xl md:text-7xl lg:text-[5.5rem] font-medium text-stone-900 dark:text-white tracking-tight leading-[1.05] mb-8 break-words hyphens-auto">
                    Your time, <br />
                    <span className="italic font-light text-stone-600 dark:text-slate-400">finally reclaimed.</span>
                  </h1>

                  <p className="font-body text-lg text-stone-600 dark:text-slate-400 max-w-lg mb-10 leading-relaxed font-normal">
                    Capy is the intelligent layer between you and the noise. Automated scheduling, email triage, and meeting representation.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button onClick={onDeployClick} icon={ArrowRight} className="h-14 px-8 text-base w-full sm:w-auto">
                      Deploy Assistant
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => window.open('https://youtu.be/dpmpxQhIYUg', '_blank', 'noopener,noreferrer')}
                      className="h-14 px-8 text-base w-full sm:w-auto"
                    >
                      View Demo
                    </Button>
                  </div>
                </FadeInWhenVisible>
              </div>

              {/* Mascot Container */}
              <div className="relative h-[380px] md:h-[550px] lg:h-[650px] w-full flex items-center justify-center lg:justify-end group">
                <FadeInWhenVisible delay={200}>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[280px] md:w-[350px] md:h-[350px] bg-stone-200/50 dark:bg-slate-800/50 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] h-[320px] md:w-[450px] md:h-[450px] border border-stone-200 dark:border-slate-800 rounded-full animate-[ping_3s_ease-in-out_infinite]" />

                  <div className="relative z-10 w-full h-full transition-transform hover:scale-[1.05] duration-500 scale-90 md:scale-110 origin-center">
                    <CapyBotMascot
                      darkMode={darkMode}
                      onDeployClick={onDeployClick}
                      isLoggedIn={isLoggedIn}
                    />
                  </div>
                </FadeInWhenVisible>
              </div>

            </div>
          </div>
        </section>

        {/* Brand Strip */}
        <div className="border-b border-stone-100 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
          <div className="container mx-auto px-6 py-10">
            <p className="text-center font-body text-xs font-bold uppercase tracking-widest text-stone-300 dark:text-slate-600 mb-8">Seamless Integration With</p>
            <div className="flex flex-wrap justify-center gap-x-12 md:gap-x-20 gap-y-6 opacity-40 dark:opacity-50 hover:opacity-80 transition-all duration-500 grayscale">
              {['Google Workspace', 'Microsoft 365', 'Slack', 'Zoom', 'Notion'].map(brand => (
                <span key={brand} className="text-xl md:text-2xl font-bold font-heading text-stone-900 dark:text-slate-200">{brand}</span>
              ))}
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-24 bg-transparent">
          <div className="container max-w-6xl mx-auto px-6">
            <FadeInWhenVisible>
              <div className="text-center mb-16">
                <h2 className="font-heading text-3xl md:text-5xl font-medium text-stone-900 dark:text-white mb-6">How Capy Works</h2>
                <p className="font-body text-lg text-stone-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed">
                  Seamlessly connecting your communication channels to your calendar and meetings.
                </p>
              </div>
              <div className="w-full overflow-x-auto md:overflow-visible">
                <HowItWorksDiagram darkMode={darkMode} />
              </div>
            </FadeInWhenVisible>
          </div>
        </section>

        {/* Bento Grid Features - FIXED LAYOUT */}
        <section id="features" className="py-24 px-6 border-t border-stone-100 dark:border-slate-800 bg-white/60 dark:bg-slate-900/40 backdrop-blur-3xl">
          <div className="container max-w-6xl mx-auto">
            <FadeInWhenVisible>
              <div className="mb-20 max-w-2xl">
                <h2 className="font-heading text-4xl md:text-5xl font-medium text-stone-900 dark:text-white tracking-tight mb-6">
                  Complete autonomy. <br />
                  <span className="italic text-stone-500 dark:text-slate-500">Zero friction.</span>
                </h2>
                <p className="font-body text-lg text-stone-600 dark:text-slate-300 leading-relaxed">
                  We replaced the complex dashboard with a conversation. Capy handles the logistics so you can focus on the decisions.
                </p>
              </div>
            </FadeInWhenVisible>

            {/* GRID FIX:
              Desktop (3 cols):
              - Lightning: col-span-2 (Takes 2 slots top left)
              - Twin: row-span-2 (Takes 1 slot right, spans down)
              - Proxy: col-span-1 (Takes 1 slot bottom left)
              - Private: col-span-1 (Takes 1 slot bottom middle)
              Total: 3x2 Grid, completely filled.
            */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 auto-rows-[minmax(320px,auto)]">

              {/* Lightning (Top Left, Wide) */}
              <FadeInWhenVisible delay={100} className="md:col-span-2 h-full">
                <BentoCard
                  className=""
                  icon={Zap}
                  title="Lightning Scheduling"
                  desc="No more email ping-pong. Capy negotiates times across calendars, timezones, and preferences instantly."
                />
              </FadeInWhenVisible>

              {/* Digital Twin (Right, Tall) */}
              <FadeInWhenVisible delay={200} className="md:row-span-2 h-full">
                <BentoCard
                  className="h-full"
                  icon={Bot}
                  title="Digital Twin"
                  desc="Capy learns your voice and communication style. It can draft replies and handle routine inquiries with 99% persona accuracy."
                />
              </FadeInWhenVisible>

              {/* Meeting Proxy (Bottom Left, Square) */}
              <FadeInWhenVisible delay={300} className="h-full">
                <BentoCard
                  className="h-full"
                  icon={Mic}
                  title="Meeting Proxy"
                  desc="Can't make it? Capy joins, records, summarizes, and can even ask pre-set questions on your behalf."
                />
              </FadeInWhenVisible>

              {/* Private (Bottom Middle, Square) - FIXED: span-1 to fit gap */}
              <FadeInWhenVisible delay={400} className="md:col-span-1 h-full">
                <BentoCard
                  className="h-full"
                  icon={Shield}
                  title="Private by Default"
                  desc="Enterprise-grade encryption. Your data never trains public models. We adhere to strict SOC2 compliance."
                />
              </FadeInWhenVisible>
            </div>
          </div>
        </section>

        {/* Integration/Interface Mockup */}
        <section className="py-32 bg-stone-50 dark:bg-slate-950 overflow-hidden border-t border-stone-200 dark:border-slate-900">
          <div className="container max-w-6xl mx-auto px-6">
            <div className="flex flex-col lg:flex-row items-center gap-20">
              <div className="flex-1 space-y-10">
                <h2 className="font-heading text-4xl md:text-6xl font-medium text-stone-900 dark:text-white tracking-tight leading-[1.1]">
                  The interface is <br />
                  <span className="italic text-stone-500 dark:text-slate-400">conversation.</span>
                </h2>
                <p className="font-body text-stone-600 dark:text-slate-300 text-xl font-normal leading-relaxed">
                  Forget complex CRMs and scheduling tools. Just tell Capy what needs to happen. It works where you work—WhatsApp, Slack, or Email.
                </p>

                <ul className="space-y-5 pt-6">
                  {[
                    'Detects urgency in incoming emails',
                    'Drafts context-aware responses',
                    'Updates Notion/CRM automatically',
                    'Follows up on stale threads'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-lg text-stone-800 dark:text-slate-200 font-medium">
                      <CheckCircle2 className="w-6 h-6 text-stone-900 dark:text-white shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mock Chat Interface */}
              <div className="flex-1 w-full max-w-xl relative">
                <FadeInWhenVisible delay={200}>
                  <div className="absolute inset-0 bg-stone-200 dark:bg-slate-800 transform rotate-3 rounded-[3rem] scale-105 -z-10 max-w-[92vw] sm:max-w-full mx-auto" />

                  <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-stone-300/50 dark:shadow-black/50 border border-stone-200 dark:border-slate-700 overflow-hidden relative z-10">
                    <div className="p-5 border-b border-stone-100 dark:border-slate-800 flex justify-between items-center bg-white/90 dark:bg-slate-900/90 backdrop-blur-md">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-stone-900 dark:bg-white flex items-center justify-center text-white dark:text-black font-heading font-bold shrink-0">HC</div>
                        <div>
                          <p className="font-bold text-stone-900 dark:text-white">Capy Assistant</p>
                          <p className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                            <span className="relative flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </span>
                            Active Now
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="p-6 md:p-8 space-y-8 bg-stone-50 dark:bg-slate-900">
                      <div className="flex justify-end">
                        <div className="bg-white border border-stone-200 dark:bg-slate-800 dark:border-slate-700 text-stone-900 dark:text-white px-5 py-4 md:px-6 md:py-4 rounded-3xl rounded-tr-sm text-sm md:text-[15px] font-medium shadow-sm">
                          Clear my afternoon. Something urgent came up.
                        </div>
                      </div>

                      <div className="flex justify-start">
                        <div className="bg-stone-900 dark:bg-slate-800 text-white dark:text-slate-100 px-5 py-5 md:px-6 rounded-3xl rounded-tl-sm text-sm md:text-[15px] max-w-[95%] shadow-lg">
                          <p className="mb-3 font-heading text-lg">I'm on it. Action plan:</p>
                          <ul className="list-disc pl-5 space-y-2 font-body opacity-90">
                            <li>Rescheduled "Q3 Review" to tomorrow 10 AM.</li>
                            <li>Emailed Sarah notifying her of the delay.</li>
                            <li>Blocked calendar until 5 PM.</li>
                          </ul>
                          <div className="mt-5 flex flex-wrap gap-3">
                            <button className="text-xs md:text-sm font-bold bg-white text-stone-900 px-5 py-2.5 rounded-full hover:bg-stone-200 transition-colors shadow-sm">Confirm Plan</button>
                            <button className="text-xs md:text-sm font-bold text-stone-300 hover:text-white px-3 py-2 transition-colors">Edit</button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-5 border-t border-stone-100 dark:border-slate-800 bg-white dark:bg-slate-900">
                      <div className="h-14 bg-stone-50 dark:bg-slate-800 rounded-full w-full border border-stone-200 dark:border-slate-700 flex items-center px-5 gap-3">
                        <Mic className="text-stone-400 dark:text-slate-400" />
                        <span className="text-stone-400 dark:text-slate-300 font-medium">Type or speak...</span>
                      </div>
                    </div>
                  </div>
                </FadeInWhenVisible>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-stone-950 dark:bg-slate-950 text-stone-400 py-20 px-6 border-t border-stone-900">
          <div className="container max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-12 mb-16">
              <div className="col-span-2">
                <span className="text-3xl font-heading font-medium text-white tracking-tight mb-6 block">
                  HiCapy<span className="text-stone-600">.</span>
                </span>
                <p className="max-w-sm text-base leading-relaxed font-body text-stone-500">
                  The AI assistant that respects your time and your data. Built for professionals who value focus over noise.
                </p>
              </div>

              <div>
                <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-xs">Product</h4>
                <ul className="space-y-4 text-sm font-medium">
                  <li><a href="/capabilities" className="hover:text-white transition-colors">Capabilities</a></li>
                  <li><a href="/security" className="hover:text-white transition-colors">Security & SOC2</a></li>
                  <li><a href="/enterprise" className="hover:text-white transition-colors">Enterprise</a></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-xs">Company</h4>
                <ul className="space-y-4 text-sm font-medium">
                  <li><a href="/about" className="hover:text-white transition-colors">About Us</a></li>
                  <li><a href="/manifesto" className="hover:text-white transition-colors">Manifesto</a></li>
                  <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                  <li><a href="/termsofuse" className="hover:text-white transition-colors">Terms of Use</a></li>
                </ul>
              </div>
            </div>

            <div className="pt-8 border-t border-stone-900 flex flex-col md:flex-row justify-between items-center gap-6 text-sm font-medium">
              <p>&copy; 2024 CapyBot Inc.</p>
              <div className="flex gap-8">
                <a href="/coming-soon" className="hover:text-white transition-colors">Twitter / X</a>
                <a href="/coming-soon" className="hover:text-white transition-colors">LinkedIn</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
      <WaitingListModal
        isOpen={showWaitlist}
        onClose={() => setShowWaitlist(false)}
        darkMode={darkMode}
      />
    </div>
  );
}
